import { action, computed, makeObservable, observable } from 'mobx'
import { AlertFactory } from 'alerts/Alert'
import MemberStore from 'iam/MemberStore'
import { SearchableUser } from 'iam/models/SearchableUser'
import RoleStore from 'iam/RoleStore'
import SearchableUserStore from 'iam/SearchableUserStore'
import { GetCurrentMember } from 'iam/SessionStore'
import SkillStore from 'iam/SkillStore'
import TeamStore from 'iam/TeamStore'
import TeamRoleStore from 'iam/TeamRoleStore'
import UserDataStore from 'iam/UserDataStore'
import WorkspaceMembershipStore from 'iam/WorkspaceMembershipStore'
import { FilterStorage } from 'infra/FiltersViewModel'
import link from 'infra/link'
import { Storage } from 'infra/storage'
import { Id } from 'types/definitions'
import { EditUserRoleDialogViewModel } from '../EditUserRoleDialog/EditUserRoleDialogViewModel'
import { ManageSkillsViewModel } from '../ManageSkillsDialog/ManageSkillsViewModel'
import { ManageUserCapacityViewModel } from '../ManageUserCapacityDialog/ManageUserCapacityViewModel'
import { RemoveFromWorkspaceViewModel } from '../RemoveFromWorkspaceDialog/RemoveFromWorkspaceViewModel'
import { WorkspaceInvitationViewModel } from '../WorkspaceInvitationDialog/WorkspaceInvitationViewModel'
import { UserSearchViewModel } from '../../UserSearchViewModel'
import { WorkspaceSelectorViewModel } from '../../WorkspaceSelectorViewModel'
import { RestoreToWorkspaceViewModel } from '../RestoreToWorkspaceDialog/RestoreToWorkspaceViewModel'
import { ManageSkillsListViewModel } from '../ManageSkillsListDialog/ManageSkillsListViewModel'
import WorkspaceSelectorState from 'iam/views/AuthenticationContainer/WorkspaceSelector/WorkspaceSelectorState'
import { CustomerStore } from 'iam/CustomerStore'

export interface UserColumn {
  id: string
  header: string
  modifyVisibility: boolean
  sortable: boolean
  visible: boolean
}

const UserManagementColumns: Array<UserColumn> = [
  {
    id: 'name',
    header: 'Name',
    modifyVisibility: true,
    visible: true,
    sortable: false,
  },
  {
    id: 'email',
    header: 'Email',
    modifyVisibility: true,
    visible: true,
    sortable: false,
  },
  {
    id: 'userType',
    header: 'User Type',
    modifyVisibility: true,
    visible: true,
    sortable: false,
  },
  {
    id: 'lastLogin',
    header: 'Last Login',
    modifyVisibility: true,
    visible: true,
    sortable: false,
  },
  {
    id: 'dateCreated',
    header: 'Date Created',
    modifyVisibility: true,
    visible: true,
    sortable: false,
  },
]

export const enum SelectionState {
  /**
   * Nothing is selected at all.
   */
  NoneSelected = 'NoneSelected',
  /**
   * Some are selected.
   */
  SomeSelected = 'SomeSelected',
  /**
   * All are selected.
   */
  AllSelected = 'AllSelected',
}

export class UserManagementViewModel {
  @observable
  selectedUserIds = new Set<Id>()

  columns = UserManagementColumns

  userSearch = new UserSearchViewModel(
    this.searchableUserStore,
    this.roleStore,
    (ns) => new FilterStorage(this.localStorage, ns),
    () => this.workspaceSelector.setDefaultWorkspace(),
  )

  workspaceSelector = new WorkspaceSelectorViewModel(
    this.getCurrentMember,
    this.workspaceMembershipStore.getCurrentUserWorkspaceMemberships,
    () => this.userSearch.workspaceFilter,
    () => this.selectedUserIds.clear(),
    () => this.userSearch.clearWorkspaceScopedFilters(),
    this.workspaceSelectorState,
  )

  workspaceInvitationVM = new WorkspaceInvitationViewModel(
    () => this.workspaceSelector.selectedWorkspace,
    () => this.selectedUserIds,
    () => this.users,
    () => this.searchableUserStore.searchableUsers,
    this.workspaceMembershipStore.getCurrentUserWorkspaceMemberships,
    this.roleStore,
    this.skillStore,
    this.teamStore,
    this.teamRoleStore,
    this.memberStore,
    this.customerStore,
    this.newAlert,
  )

  manageUserCapacityVM = new ManageUserCapacityViewModel(
    this.memberStore,
    this.userDataStore,
    this.newAlert,
    this.getCurrentMember,
  )

  editUserRoleVM = new EditUserRoleDialogViewModel(
    this.roleStore,
    this.memberStore,
    this.newAlert,
  )

  manageSkillsVM = new ManageSkillsViewModel(
    () => this.workspaceSelector.selectedWorkspace,
    () => this.selectedUserIds,
    () => this.users,
    () => this.toggleUser,
    this.memberStore,
    this.skillStore,
    this.workspaceMembershipStore,
    this.newAlert,
  )

  manageSkillsListVM = new ManageSkillsListViewModel(
    this.memberStore,
    this.skillStore,
    this.newAlert,
  )

  removeFromWorkspaceVM = new RemoveFromWorkspaceViewModel(
    this.memberStore,
    this.newAlert,
    this.removeUsers.bind(this),
  )

  restoreToWorkspaceVM = new RestoreToWorkspaceViewModel(
    this.memberStore,
    this.newAlert,
    this.removeUsers.bind(this),
  )

  removeUsers(userIds: Id[]) {
    this.userSearch.removeUsers(userIds)
    this.selectedUserIds.clear()
  }

  constructor(
    private getCurrentMember: GetCurrentMember,
    private localStorage: Storage,
    private memberStore: MemberStore,
    private roleStore: RoleStore,
    private searchableUserStore: SearchableUserStore,
    private skillStore: SkillStore,
    private teamStore: TeamStore,
    private teamRoleStore: TeamRoleStore,
    private userDataStore: UserDataStore,
    private workspaceMembershipStore: WorkspaceMembershipStore,
    private customerStore: CustomerStore,
    private newAlert: AlertFactory,
    private workspaceSelectorState: WorkspaceSelectorState,
  ) {
    makeObservable(this)
  }

  isSelected = (userId: Id) => this.selectedUserIds.has(userId)

  activate() {
    this.manageSkillsVM.activate()
    this.userSearch.activate()
    this.workspaceSelector.activate()
    this.workspaceInvitationVM.activate()
  }

  deactivate() {
    this.manageSkillsVM.deactivate()
    this.userSearch.deactivate()
    this.workspaceSelector.deactivate()
    this.workspaceInvitationVM.deactivate()
  }

  @computed
  get isPlatformAdmin() {
    return this.getCurrentMember().user.platformAdmin
  }

  @computed
  get isSearchFilterApplied() {
    const isSearching = this.userSearch.searchText.value !== ''
    const haveFiltersApplied = this.userSearch.filters.appliedFilterCount > 0

    return isSearching || haveFiltersApplied
  }

  @computed
  get shouldShowEmptyState() {
    return this.userSearch.scrollProvider.isDone && this.users.length === 0
  }

  @computed
  get canAccessUserManagement() {
    return this.workspaceMembershipStore
      .getCurrentUserWorkspaceMemberships()
      .some((wms) => wms.member.hasPermission('ADMIN_USER_LIST'))
  }

  @computed
  get selectedState() {
    if (this.selectedUserIds.size === 0) {
      return SelectionState.NoneSelected
    } else if (this.users.length === this.selectedUserIds.size) {
      return SelectionState.AllSelected
    } else {
      return SelectionState.SomeSelected
    }
  }

  @computed
  get users() {
    return this.userSearch.scrollProvider.items
  }

  @action.bound
  toggleUser(checked: boolean, userId: Id) {
    if (checked) {
      this.selectedUserIds.add(userId)
    } else {
      this.selectedUserIds.delete(userId)
    }
  }

  @action.bound
  toggleAll() {
    switch (this.selectedState) {
      case SelectionState.AllSelected:
        this.selectedUserIds.clear()
        break
      case SelectionState.NoneSelected:
      case SelectionState.SomeSelected:
        this.selectedUserIds = new Set(this.users.map((user) => user.publicId))
        break
    }
  }

  /**
   * Clears any search filters.
   */
  @action.bound
  clearFilters() {
    this.userSearch.searchText.clear()
    this.userSearch.filters.clearExplicitFilters()
  }

  getUserDetailsLinkForUser(user: SearchableUser) {
    return link.userDetails(Id.assert(user.publicId))
  }
}
